import React, { useState, useEffect } from 'react';
import { ShieldAlert, Store, AlertTriangle, Plus, Trash2, Save, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { updateSystemSettings } from '../../services/configService';

export default function SystemSettings() {
  const { isSuperAdmin, systemSettings } = useAuth();
  
  const [localSettings, setLocalSettings] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Yeni Şube State
  const [newBranchId, setNewBranchId] = useState('');
  const [newBranchName, setNewBranchName] = useState('');
  const [newBranchColor, setNewBranchColor] = useState('from-navy-600 to-navy-800');

  useEffect(() => {
    if (systemSettings) {
      setLocalSettings(JSON.parse(JSON.stringify(systemSettings)));
    }
  }, [systemSettings]);

  if (!isSuperAdmin) {
    return <div className="p-8 text-white">Yetersiz Yetki</div>;
  }

  if (!localSettings) {
    return <div className="p-8 text-navy-400">Yükleniyor...</div>;
  }

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await updateSystemSettings(localSettings);
      alert("Sistem ayarları başarıyla güncellendi.");
    } catch (err) {
      alert("Ayarlar güncellenirken hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddBranch = () => {
    if (!newBranchId.trim() || !newBranchName.trim()) {
      alert("Lütfen şube ID'si ve adını giriniz.");
      return;
    }
    
    // Aynı ID var mı kontrolü
    if (localSettings.systemBranches?.some(b => b.id.toLowerCase() === newBranchId.trim().toLowerCase())) {
      alert("Bu ID ile kayıtlı bir şube zaten var.");
      return;
    }

    const newBranch = {
      id: newBranchId.trim().toLowerCase(),
      name: newBranchName.trim(),
      color: newBranchColor
    };

    setLocalSettings(prev => ({
      ...prev,
      systemBranches: [...(prev.systemBranches || []), newBranch]
    }));

    setNewBranchId('');
    setNewBranchName('');
  };

  const handleRemoveBranch = (id) => {
    if (window.confirm("Bu şubeyi silmek istediğinize emin misiniz? (Önceden eklenen kurbanların verisi bozulmaz, ancak dropdownlarda görünmeyebilir)")) {
      setLocalSettings(prev => ({
        ...prev,
        systemBranches: prev.systemBranches.filter(b => b.id !== id)
      }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-navy-800 p-6 rounded-2xl border border-navy-700/50">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-gold-400" />
            Sistem Ayarları
          </h1>
          <p className="text-sm text-navy-400 mt-1">Platformun ana konfigürasyon ve kriz yönetimi merkezi.</p>
        </div>
        <button 
          onClick={handleSaveSettings}
          disabled={isSaving}
          className="flex items-center gap-2 px-5 py-2.5 bg-forest-600 hover:bg-forest-500 rounded-xl text-white font-bold transition-all shadow-lg shadow-forest-500/20 disabled:opacity-50"
        >
          <Save className="w-5 h-5" />
          {isSaving ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* A Bölümü: SİSTEM ŞALTERLERİ */}
        <div className="glass rounded-2xl border border-navy-700/50 p-6">
          <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-6 pb-4 border-b border-navy-700/50">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            Operasyonel Şalterler
          </h2>
          
          <div className="space-y-6">
            {/* Maintenance Mode */}
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <h3 className="text-white font-bold text-base">Sistem Bakım Modu</h3>
                <p className="text-xs text-navy-400 leading-relaxed mt-1">Sistemi dışarıya kapatır. Tüm müşteriler ve standart adminler ekranda "Bakım Çalışması" sayfası görür.</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  value="" 
                  className="sr-only peer"
                  checked={localSettings.isSystemMaintenance || false}
                  onChange={(e) => setLocalSettings(prev => ({...prev, isSystemMaintenance: e.target.checked}))}
                />
                <div className="w-14 h-7 bg-navy-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-red-500 shadow-inner"></div>
              </label>
            </div>

            {/* Catalog Closed Toggle */}
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <h3 className="text-white font-bold text-base">Katalog Satışlarını Durdur</h3>
                <p className="text-xs text-navy-400 leading-relaxed mt-1">Müşteriler kendi hisselerini görebilir ancak Kurban Pazarı / Katalog tarafı kapanır ve alım gerçekleştirilemez. (Kotalar dolduğunda kullanılır)</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  value="" 
                  className="sr-only peer"
                  checked={localSettings.isCatalogClosed || false}
                  onChange={(e) => setLocalSettings(prev => ({...prev, isCatalogClosed: e.target.checked}))}
                />
                <div className="w-14 h-7 bg-navy-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-orange-500 shadow-inner"></div>
              </label>
            </div>
          </div>
        </div>

        {/* B Bölümü: ŞUBE VE MERKEZ YÖNETİMİ */}
        <div className="glass rounded-2xl border border-navy-700/50 p-6 flex flex-col">
          <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-6 pb-4 border-b border-navy-700/50">
            <Store className="w-5 h-5 text-blue-400" />
            Şube Yönetimi
          </h2>
          
          <div className="flex-1 overflow-y-auto pr-2 space-y-3 mb-6">
             {localSettings.systemBranches?.length === 0 && (
                <div className="text-center text-navy-400 text-sm py-4">Kayıtlı şube bulunamadı.</div>
             )}
             {localSettings.systemBranches?.map((branch) => (
                <div key={branch.id} className="flex items-center justify-between bg-navy-800 p-3 rounded-xl border border-navy-700/50">
                   <div className="flex items-center gap-3">
                     <span className={`w-3 h-3 rounded-full bg-gradient-to-br ${branch.color}`}></span>
                     <div>
                       <div className="text-white text-sm font-bold">{branch.name}</div>
                       <div className="text-[10px] text-navy-400 font-mono mt-0.5">ID: {branch.id}</div>
                     </div>
                   </div>
                   <button 
                     onClick={() => handleRemoveBranch(branch.id)}
                     className="p-2 text-navy-400 hover:text-red-400 hover:bg-navy-700 rounded-lg transition-colors"
                   >
                     <Trash2 className="w-4 h-4" />
                   </button>
                </div>
             ))}
          </div>

          <div className="p-4 bg-navy-900 border border-navy-700 rounded-xl space-y-3">
             <h3 className="text-xs font-bold text-navy-300 uppercase tracking-wider mb-2">Yeni Şube Ekle</h3>
             <div className="grid grid-cols-2 gap-3">
               <div>
                  <label className="text-[10px] text-navy-400 block mb-1">Şube ID (Boşluksuz)</label>
                  <input 
                    type="text" 
                    value={newBranchId}
                    onChange={(e) => setNewBranchId(e.target.value.toLowerCase().replace(/\s+/g, ''))}
                    placeholder="örn: kartal" 
                    className="w-full bg-navy-950 border border-navy-700 rounded-lg px-3 py-2 text-sm text-white"
                  />
               </div>
               <div>
                  <label className="text-[10px] text-navy-400 block mb-1">Şube Adı (Görünen)</label>
                  <input 
                    type="text" 
                    value={newBranchName}
                    onChange={(e) => setNewBranchName(e.target.value)}
                    placeholder="örn: Kartal Çiftliği" 
                    className="w-full bg-navy-950 border border-navy-700 rounded-lg px-3 py-2 text-sm text-white"
                  />
               </div>
             </div>
             <div>
                <label className="text-[10px] text-navy-400 block mb-1">Renk Seçimi</label>
                <select 
                  value={newBranchColor}
                  onChange={(e) => setNewBranchColor(e.target.value)}
                  className="w-full bg-navy-950 border border-navy-700 rounded-lg px-3 py-2 text-sm text-white"
                >
                  <option value="from-forest-600 to-forest-800">Yeşil Tonları</option>
                  <option value="from-blue-600 to-blue-800">Mavi Tonları</option>
                  <option value="from-gold-500 to-gold-700">Altın/Sarı Tonları</option>
                  <option value="from-purple-600 to-purple-800">Mor Tonları</option>
                  <option value="from-red-600 to-red-800">Kırmızı Tonları</option>
                  <option value="from-navy-600 to-navy-800">Gece Mavisi (Varsayılan)</option>
                </select>
             </div>
             <button 
               onClick={handleAddBranch}
               className="w-full mt-2 py-2 bg-navy-800 hover:bg-navy-700 border border-navy-600 rounded-lg text-white text-sm font-bold flex items-center justify-center gap-2 transition-colors focus:ring-1 focus:ring-forest-500"
             >
               <Plus className="w-4 h-4" /> Şubeyi Ekle
             </button>
          </div>
        </div>

      </div>
    </div>
  );
}
